import * as DialogPrimitives from '@kobalte/core/dialog'
import { createSignal, Show } from 'solid-js'
import { CakeBoxIcon } from '~/components/icon/cake-box'
import { CoinIcon } from '~/components/icon/coin'
import { GemIcon } from '~/components/icon/gem'
import { Spinner } from '~/components/spinner'
import type { ExtraBoxOption } from '~/lib/derive-extra-boxes'
import { m } from '~/paraglide/messages'

export function ExtraBoxOpenConfirmDialog(props: {
  currency: 'coin' | 'gem'
  option: ExtraBoxOption
  onConfirm: () => void | Promise<void>
  onCancel: () => void
}) {
  const [isLoading, setIsLoading] = createSignal(false)

  const description = () =>
    m.dailog_open_confirmation_content({
      number: String(props.option.price),
      currency: props.currency === 'coin' ? m.coin() : m.time_crystal(),
    })

  const handlePrimary = () => {
    const result = props.onConfirm()
    if (result instanceof Promise) {
      setIsLoading(true)
      // Guarantee a minimum visible time so the loader never flashes when
      // the network (or MSW) resolves in <100ms.
      const minVisible = new Promise<void>((r) => setTimeout(r, 500))
      Promise.allSettled([result, minVisible]).finally(() =>
        setIsLoading(false),
      )
    }
  }

  const handleSecondary = () => {
    props.onCancel()
  }

  return (
    <DialogPrimitives.Dialog
      open={true}
      onOpenChange={(isOpen) => {
        if (!isOpen && !isLoading()) props.onCancel()
      }}
    >
      <DialogPrimitives.Portal>
        <DialogPrimitives.Overlay class="fixed inset-0 z-[100] backdrop-blur-[8px] data-[expanded]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[expanded]:fade-in-0" />
        <DialogPrimitives.Content
          class="backdrop-filter-[50px] fixed left-1/2 top-1/2 z-[100] w-[calc(100vw-48px)] max-w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-[32px] border-2 border-white/30 bg-white/75 p-6 data-[expanded]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[expanded]:fade-in-0"
          style={{
            'will-change': 'backdrop-filter',
            ['--tw-enter-translate-x' as never]: '-50%',
            ['--tw-enter-translate-y' as never]: '-50%',
            ['--tw-exit-translate-x' as never]: '-50%',
            ['--tw-exit-translate-y' as never]: '-50%',
          }}
          onInteractOutside={(e) => {
            e.preventDefault()
          }}
          onOpenAutoFocus={(e) => {
            e.preventDefault()
          }}
        >
          <div class="flex flex-col items-center gap-6">
            <CakeBoxIcon class="h-[96px] w-[84px]" />
            <div class="flex flex-col gap-2 text-center">
              <DialogPrimitives.Title class="text-lg font-semibold text-[#333333]">
                {m.dailog_open_confirmation_title()}
              </DialogPrimitives.Title>
              <p class="whitespace-pre-line break-words text-base text-[#666666]">
                {description()}
              </p>
            </div>

            <div class="flex w-full flex-col gap-2">
              <button
                type="button"
                class="relative flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-[#176361] text-base font-bold text-white active:bg-[#0f4d4b] disabled:opacity-70"
                disabled={isLoading()}
                onClick={handlePrimary}
              >
                <span>{m.btn_open()}</span>
                <Show when={props.currency === 'gem'} fallback={<CoinIcon />}>
                  <GemIcon />
                </Show>
                <span class="tabular-nums">{props.option.price}</span>
                <Show when={isLoading()}>
                  <span class="absolute right-4 top-1/2 -translate-y-1/2">
                    <Spinner variant="circular" class="size-5 text-white" />
                  </span>
                </Show>
              </button>
              <button
                type="button"
                class="flex h-[44px] w-full items-center justify-center rounded-xl text-base font-bold text-[#176361] disabled:opacity-70"
                disabled={isLoading()}
                onClick={handleSecondary}
              >
                {m.cancel_btn()}
              </button>
            </div>
          </div>
        </DialogPrimitives.Content>
      </DialogPrimitives.Portal>
    </DialogPrimitives.Dialog>
  )
}
